/**
 * Sound effects synthesized with the Web Audio API: no audio files, no license, a few hundred bytes. Browser only (it is a view concern:
 * never call it from src/core). The first pointer gesture unlocks audio on phones. Tune a sound by editing its row in SOUNDS.
 * Recorded or generated sound (CC0 packs, `npm run gen-audio`) plays through the same object: `await sfx.preload({ gol: 'audio/vo-gol.mp3' })`, then `sfx.sample('gol')`.
 * Register each file in assets/ASSETS.md (gen-audio does it for the ones it makes).
 */
type Note = { freq: number; to?: number; dur: number; at?: number; wave?: OscillatorType; gain?: number }
export type SfxName = 'tap' | 'drop' | 'bounce' | 'good' | 'bad' | 'win' | 'lose'

const SOUNDS: Record<SfxName, Note[]> = {
  tap: [{ freq: 620, to: 840, dur: 0.07, gain: 0.12 }],
  drop: [{ freq: 520, to: 240, dur: 0.14, wave: 'triangle', gain: 0.14 }],
  bounce: [{ freq: 1100, to: 760, dur: 0.045, wave: 'sine', gain: 0.07 }],
  good: [{ freq: 660, dur: 0.09, gain: 0.14 }, { freq: 880, dur: 0.13, at: 0.08, gain: 0.14 }],
  bad: [{ freq: 220, to: 130, dur: 0.22, wave: 'sawtooth', gain: 0.09 }],
  win: [{ freq: 523, dur: 0.12, gain: 0.14 }, { freq: 659, dur: 0.12, at: 0.11, gain: 0.14 }, { freq: 784, dur: 0.2, at: 0.22, gain: 0.14 }],
  lose: [{ freq: 330, to: 196, dur: 0.35, wave: 'triangle', gain: 0.12 }]
}

let ctx: AudioContext | null = null
let muted = false, voiceMuted = false, musicMuted = false
try { muted = localStorage.getItem('hitz-muted') === '1'; voiceMuted = localStorage.getItem('hitz-voice-muted') === '1'; musicMuted = localStorage.getItem('hitz-music-muted') === '1' } catch { /* storage blocked: stay unmuted */ }

function audio(): AudioContext | null {
  try {
    ctx ??= new AudioContext()
    if (ctx.state === 'suspended') void ctx.resume()
    decodePending()
    return ctx
  } catch { return null }
}
/** Decodes downloaded files once a context exists. Safe to call any time. */
function decodePending() {
  if (!ctx) return
  for (const [name, data] of [...raw]) {
    raw.delete(name)
    ctx.decodeAudioData(data).then((b) => buffers.set(name, b), () => { /* undecodable: skip */ })
  }
}

const buffers = new Map<string, AudioBuffer>()
const raw = new Map<string, ArrayBuffer>() // downloaded, waiting for an AudioContext (browsers only allow creating one after a tap)
let musicBus: GainNode | null = null
const bus = (a: AudioContext) => {
  if (!musicBus) { musicBus = a.createGain(); musicBus.connect(a.destination) }
  return musicBus
}

export const sfx = {
  /** Loads audio files (paths relative to the site base, e.g. `audio/vo-gol.mp3`) so `sample` can play them with no delay. Failures are ignored: the game stays silent, never broken. */
  async preload(files: Record<string, string>) {
    const base = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/'
    // Download now (no AudioContext needed, so no "not allowed to start" warning); decode on the first gesture, when `audio()` creates the context.
    await Promise.all(Object.entries(files).map(async ([name, path]) => {
      try { raw.set(name, await (await fetch(base + path)).arrayBuffer()) } catch { /* missing: skip */ }
    }))
    decodePending()
  },
  has: (name: string) => buffers.has(name),
  /** Plays a preloaded file. `rate` changes speed and pitch (0.9-1.1 keeps repeated sounds from feeling mechanical), `gain` is the volume 0..1. Returns false when it could not play. */
  sample(name: string, opts: { rate?: number; gain?: number } = {}): boolean {
    const b = buffers.get(name)
    if (muted || !b || (voiceMuted && name.startsWith('vo-'))) return false // voice lines are named vo-*: they have their own switch
    const a = audio()
    if (!a || a.state !== 'running') return false
    const src = a.createBufferSource(), g = a.createGain()
    src.buffer = b; src.playbackRate.value = opts.rate ?? 1; g.gain.value = opts.gain ?? 0.8
    src.connect(g).connect(a.destination); src.start()
    return true
  },
  /**
   * Starts a preloaded track as background music on its own bus (so voice lines can duck it). Returns a function that fades it out and stops it.
   * Music has its own switch (`musicMuted`) besides the global mute. Track files are made with `npm run gen-audio -- music <name> "<style>" --loop`.
   */
  music(name: string, gain = 0.3, fadeSec = 0.6): () => void {
    const b = buffers.get(name), a = audio()
    if (muted || musicMuted || !b || !a || a.state !== 'running') return () => {}
    const src = a.createBufferSource(), g = a.createGain(), t = a.currentTime
    src.buffer = b; src.loop = true
    g.gain.setValueAtTime(0.0001, t); g.gain.linearRampToValueAtTime(gain, t + fadeSec)
    src.connect(g).connect(bus(a)); src.start()
    return () => { const n = a.currentTime; g.gain.cancelScheduledValues(n); g.gain.setValueAtTime(g.gain.value, n); g.gain.linearRampToValueAtTime(0.0001, n + fadeSec); src.stop(n + fadeSec + 0.05) }
  },
  /** Lowers the music for a moment (a voice line, a jingle) and brings it back: `level` is the share of the music volume kept, `ms` how long it stays down. */
  duck(level = 0.35, ms = 1200) {
    if (!musicBus || !ctx) return
    const g = musicBus.gain, t = ctx.currentTime
    g.cancelScheduledValues(t); g.setValueAtTime(g.value, t); g.linearRampToValueAtTime(level, t + 0.08)
    g.linearRampToValueAtTime(1, t + 0.08 + ms / 1000)
  },
  get musicMuted() { return musicMuted },
  setMusicMuted(v: boolean) {
    musicMuted = v
    try { localStorage.setItem('hitz-music-muted', v ? '1' : '0') } catch { /* ignore */ }
  },
  /** Starts a preloaded file as a loop (ambience). Returns a function that fades it out and stops it. Respects mute at start. */
  loop(name: string, gain = 0.25): () => void {
    const b = buffers.get(name), a = audio()
    if (muted || !b || !a || a.state !== 'running') return () => {}
    const src = a.createBufferSource(), g = a.createGain()
    src.buffer = b; src.loop = true; g.gain.value = gain
    src.connect(g).connect(a.destination); src.start()
    return () => { g.gain.linearRampToValueAtTime(0.0001, a.currentTime + 0.3); src.stop(a.currentTime + 0.35) }
  },
  /** Plays one effect. Silent when muted or when the browser has no audio yet. `detune` varies the pitch (e.g. bounces) in semitones. */
  play(name: SfxName, detune = 0) {
    if (muted) return
    const a = audio()
    if (!a || a.state !== 'running') return
    const t0 = a.currentTime
    for (const n of SOUNDS[name]) {
      const o = a.createOscillator(), g = a.createGain()
      const k = Math.pow(2, detune / 12)
      o.type = n.wave ?? 'sine'
      const start = t0 + (n.at ?? 0)
      o.frequency.setValueAtTime(n.freq * k, start)
      if (n.to) o.frequency.exponentialRampToValueAtTime(n.to * k, start + n.dur)
      g.gain.setValueAtTime(n.gain ?? 0.1, start)
      g.gain.exponentialRampToValueAtTime(0.0001, start + n.dur)
      o.connect(g).connect(a.destination)
      o.start(start); o.stop(start + n.dur + 0.02)
    }
  },
  /** True once the browser lets audio play (after the first gesture): music and loops started before that are silent. */
  get running() { return !!ctx && ctx.state === 'running' },
  get muted() { return muted },
  /** Voice lines (files named `vo-*`) can be switched off on their own, because a voice that is not good is easy to drop without losing the rest of the sound. */
  get voiceMuted() { return voiceMuted },
  setVoiceMuted(v: boolean) {
    voiceMuted = v
    try { localStorage.setItem('hitz-voice-muted', v ? '1' : '0') } catch { /* ignore */ }
  },
  setMuted(v: boolean) {
    muted = v
    try { localStorage.setItem('hitz-muted', v ? '1' : '0') } catch { /* ignore */ }
  },
  /** Call once; the first pointer gesture creates and resumes the AudioContext (required by mobile browsers). */
  unlockOnGesture() { addEventListener('pointerdown', () => { audio() }, { once: true, capture: true }) }
}
